import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Stack, Typography } from '@mui/material'
import { RadioTowerIcon } from 'lucide-react'
import { isSmallSample, isSocialNetwork, MASTER_UNATTRIBUTED_LABELS, socialNetworkOfHost, type MasterChannelRow, type SocialNetwork } from '@repo/types'

import { EMPTY_VALUE, Skeleton } from '@repo/ui'
import { EmptyState, Header, LinkCell, StatusChip, Table } from 'components'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { GTM_CHANNEL_STATUSES } from 'constants/statuses'
import { useCanAccess } from 'hooks'
import { useSeoStatus, useWebsiteOverview } from 'hooks/api'
import { useSalesFlow } from 'hooks/api/useGtmSalesPipeline'
import { headerWithTerm } from 'components/display/Table/headerWithTerm'
import { INFO_HEADER_ALLOWANCE } from 'components/display/Table/headerWithInfo'

// The SUMMARY only (a few hundred bytes); the event list itself stays in the lazy page's chunk.
import { TRADE_SHOWS_SUMMARY } from '../GtmTradeShowsPage/tradeShowsSummary.generated'
import { formatIsoDay } from '../GtmTradeShowsPage/tradeShowModel'
import { ARTICLES_MANIFEST, ARTICLES_MANIFEST_PROVENANCE } from '../GtmArticlesPage/articlesManifest.generated'

import { moneyShort } from '../GtmSalesPage/dealsModel'
import { GTM_CHANNELS, type GtmChannel } from './gtmChannels'
import { ChannelsFilterBar, MasterPipelineCard } from './MasterPipelineCard'
import { inPeriod, pct, periodLabel, useMasterView } from './masterPipelineView'
import { rangeForPreset } from '../InternalAnalyticsPage/websiteAnalyticsModel'

/** The shared Table's toolbar + header + bottom border above the rows (measured at 1280). */
const TABLE_CHROME_PX = 160

/** A channel row with its pipeline figures (VRG-696), numeric for sorting. */
type ChannelRow = GtmChannel & {
  /** A Social sub-row (VRG-735) carries the same figures for its network, without a channel key. */
  pipeline: Omit<MasterChannelRow, 'key' | 'channel'> | null
  shareSort: number
  pipelineSort: number
  wonSort: number
  winSort: number
}

/** A referrer as the website analytics report it (a host, or a URL) → its host. */
const referrerHostOf = (v: string): string | null => {
  const t = v.trim()
  if (!t) return null
  try {
    return new URL(t.includes('://') ? t : `https://${t}`).hostname
  } catch {
    return null
  }
}

/** A figure with its n beside it; greyed when n is a small sample (never hidden). */
const Figure = ({ main, n, small }: { main: string; n?: string; small?: boolean }) => (
  <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
    <Typography variant="body3" noWrap sx={{ color: small ? 'text.disabled' : 'text.primary' }}>
      {main}
    </Typography>
    {n && (
      <Typography variant="body4" color="text.secondary" noWrap>
        {n}
      </Typography>
    )}
  </Stack>
)

/**
 * `/internal/gtm` — VRG-672: the Channels overview, the landing page of the GTM group.
 *
 * ONE ROW PER TRACTION CHANNEL, ALL NINETEEN, including the ones Vera has decided to skip.
 * The sidebar only lists channels Vera OPERATES on (SEO & AEO today); this page is where
 * every other channel lives, so "is anyone doing trade shows?" has one answer in one place
 * rather than being inferred from the absence of a sidebar row. Skipped channels stay
 * because "Not now" is a decision worth seeing, and dropping them would make a deliberate
 * skip indistinguishable from never having considered it.
 *
 * Outreach is a row of its own directly under Sales, carrying Sales' number: it is how Sales
 * is operated (Spencer, 2026-10-07: email outreach and field visits are its two campaign
 * types), with its own page. Sales links to the pipeline, Outreach to the campaigns. It was
 * the Field visits row until VRG-679.
 *
 * THE HEADLINE COLUMN SHOWS ONLY DATA THAT EXISTS. SEO reads the same status endpoint the
 * SEO page does; Trade shows reads its snapshot's summary (VRG-673); Content marketing reads
 * the website article manifest's counts (VRG-674). Every other channel shows "—" rather than a placeholder figure, because
 * a zero would claim a measurement nobody took.
 *
 * Static rows from `gtmChannels.ts`, whose header says where the canon lives. The table is
 * the shared one, client-side: twenty rows need no server paging, and search, sort and the
 * status filter all come free.
 */
export const GtmChannelsPage = () => {
  const { data: seo, isLoading: seoLoading, error: seoError } = useSeoStatus()
  // VRG-696 — the master pipeline and the table's pipeline columns read ONE query, the same
  // `GET /internal/gtm/sales/flow` the Sales flow chart reads, with the same filters.
  const masterApi = useMasterView()
  const { view } = masterApi
  const flow = useSalesFlow(view.filters)
  const P = periodLabel(view.period)

  const rows = useMemo<ChannelRow[]>(() => {
    const by = new Map((flow.data?.master.channels ?? []).map((r) => [r.key, r]))
    // VRG-735 — Social's deals by network, for the LinkedIn / Instagram / Other social sub-rows.
    const byNetwork = new Map((flow.data?.master.social_networks ?? []).map((r) => [r.key, r]))
    return GTM_CHANNELS.map((c) => {
      const r = c.sourceChannel ? (by.get(c.sourceChannel) ?? null) : c.socialNetwork ? (byNetwork.get(c.socialNetwork) ?? null) : null
      return {
        ...c,
        pipeline: r,
        shareSort: r?.share ?? -1,
        pipelineSort: r?.pipeline_arr ?? -1,
        wonSort: r?.won_arr ?? -1,
        winSort: r?.win_rate ?? -1,
      }
    })
  }, [flow.data])

  /** Deals no channel row can claim: Expansion, website with no referrer, and not recorded. */
  const unattributed = useMemo(
    () => (flow.data?.master.channels ?? []).filter((r) => r.channel === null && r.deals > 0),
    [flow.data],
  )

  /** VRG-735 — Social deals with no network named (set to Social by hand, no referrer or utm to read). */
  const socialNotSplit = flow.data?.master.social_networks?.find((r) => r.key === 'not_split')?.deals ?? 0

  // VRG-735 — website visits per social network: the website analytics referrers (Vercel), over
  // the page's range, or the last 30 days when the page shows all time. Analytics access only;
  // anyone else sees "—". A referrer is filed by the same host list as the deals.
  const { isHasAccess: canSeeAnalytics } = useCanAccess({ action: 'internal_analytics_view' })
  const visitsRange = useMemo(() => rangeForPreset(view.preset === 'all' ? '30d' : view.preset), [view.preset])
  const visitsRangeLabel = view.preset === 'all' ? 'last 30 days' : 'in this range'
  const overview = useWebsiteOverview(visitsRange, canSeeAnalytics)
  const visits = useMemo(() => {
    const o = overview.data
    if (!o || o.sources.vercel.status !== 'ok') return null
    const out: Record<SocialNetwork, number> = { linkedin: 0, instagram: 0, other_social: 0 }
    for (const r of o.breakdown.referrers) {
      const n = socialNetworkOfHost(referrerHostOf(r.value))
      if (n && typeof r.visitors === 'number') out[n] += r.visitors
    }
    return out
  }, [overview.data])
  const visitsUnavailable = !canSeeAnalytics
    ? 'Website visits need Analytics access'
    : overview.error
      ? 'Website visits could not be loaded'
      : overview.data && overview.data.sources.vercel.status !== 'ok'
        ? `Website visits need Vercel analytics (${overview.data.sources.vercel.status.replace('_', ' ')})`
        : null

  // VRG-735 — `?social=linkedin|instagram|other_social` (Social → Metrics links here) marks that
  // sub-row and scrolls it into view once the table has drawn it.
  const [params] = useSearchParams()
  const focusNetwork = params.get('social')
  const focusRowId = isSocialNetwork(focusNetwork) ? GTM_CHANNELS.find((c) => c.socialNetwork === focusNetwork)?.id ?? null : null
  useEffect(() => {
    if (!focusRowId) return
    const t = window.setTimeout(() => document.querySelector(`[data-id="${focusRowId}"]`)?.scrollIntoView({ block: 'center' }), 300)
    return () => window.clearTimeout(t)
  }, [focusRowId])

  const seoHeadline = useMemo(() => {
    if (!seo) return null
    const tracked = Object.values(seo.counts.trackedByMarket ?? {}).reduce((a, b) => a + b, 0)
    return {
      primary: `${tracked.toLocaleString('en-US')} tracked keywords`,
      secondary: `${seo.spend.credits.toLocaleString('en-US')} of ${seo.spend.creditCap.toLocaleString('en-US')} credits this month`,
    }
  }, [seo])

  const columns = useMemo(
    () => [
      {
        field: 'number',
        headerName: '#',
        // NOT `type: 'number'`: in the shared Table a number column's header collapsed its
        // title to 1px beside the sort glyph, so "#" never rendered (measured at 1280, §49).
        // The default string column sorts these numerically anyway (MUI's string-or-number
        // comparator compares two numbers as numbers).
        // 104 = the shared Table's 24px first-column gutter (`.firstDataCol`) + cell padding +
        // "#" + the 28px sort button. At 80 the title collapsed to 1px (§49, measured).
        width: 104,
        disableReorder: true,
        pinned: 'left',
        // A sub-channel repeats its parent's number; show it once, on the parent.
        renderCell: ({ row }: { row: GtmChannel }) => (row.parentId ? '' : row.number),
      },
      {
        field: 'name',
        headerName: 'Channel',
        type: 'string',
        width: 184,
        // VRG-696: pinned with the Page column, so a row stays named while the figures scroll.
        pinned: 'left',
        disableReorder: true,
        renderCell: ({ row }: { row: GtmChannel }) =>
          row.parentId ? (
            <Stack sx={{ pl: 4, minWidth: 0 }} justifyContent="center" height="100%">
              <Typography variant="body3" noWrap>
                {row.name}
              </Typography>
              <Typography variant="body4" color="text.secondary" noWrap>
                Part of {GTM_CHANNELS.find((c) => c.id === row.parentId)?.name}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body3" noWrap>
              {row.name}
            </Typography>
          ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(GTM_CHANNEL_STATUSES),
        width: 120,
        renderCell: ({ row }: { row: GtmChannel }) => (
          <StatusChip statuses={GTM_CHANNEL_STATUSES} status={row.status} size="small" />
        ),
      },
      // ── VRG-696: the pipeline columns, same slice and filters as the master chart ──
      {
        field: 'shareSort',
        headerName: 'Share of deals',
        width: 152 + INFO_HEADER_ALLOWANCE,
        renderHeader: headerWithTerm('Share of deals', 'first-touch-attribution'),
        renderCell: ({ row }: { row: ChannelRow }) => {
          if (row.parentId && !row.socialNetwork) return ''
          const r = row.pipeline
          if (!flow.data) return flow.isLoading ? <Skeleton height={14} width="60%" /> : EMPTY_VALUE
          if (!r) return <Figure main="0%" n="n 0" small />
          return <Figure main={pct(r.deals, flow.data.master.total.n)} n={`n ${r.deals}`} small={isSmallSample(r.deals)} />
        },
      },
      {
        field: 'pipelineSort',
        // "Pipeline value" in the glossary (the tooltip); the header says the period instead.
        headerName: `Pipeline ${P}`,
        width: 140 + INFO_HEADER_ALLOWANCE,
        renderHeader: headerWithTerm(`Pipeline ${P}`, 'pipeline-value'),
        renderCell: ({ row }: { row: ChannelRow }) => {
          if (row.parentId && !row.socialNetwork) return ''
          const r = row.pipeline
          if (!flow.data) return flow.isLoading ? <Skeleton height={14} width="60%" /> : EMPTY_VALUE
          if (!r || r.pipeline_n === 0) return EMPTY_VALUE
          return (
            <Figure
              // Every open deal without a value: there is no figure, not a figure of $0.
              main={r.pipeline_without_value === r.pipeline_n ? EMPTY_VALUE : moneyShort(inPeriod(r.pipeline_arr, view.period))}
              n={`${r.pipeline_n} open${r.pipeline_without_value ? `, ${r.pipeline_without_value} no value` : ''}`}
            />
          )
        },
      },
      {
        field: 'wonSort',
        headerName: `Won ${P}`,
        width: 124 + INFO_HEADER_ALLOWANCE,
        renderHeader: headerWithTerm(`Won ${P}`, 'won-revenue'),
        renderCell: ({ row }: { row: ChannelRow }) => {
          if (row.parentId && !row.socialNetwork) return ''
          const r = row.pipeline
          if (!flow.data) return flow.isLoading ? <Skeleton height={14} width="60%" /> : EMPTY_VALUE
          if (!r || r.won_n === 0) return EMPTY_VALUE
          return (
            <Figure
              main={moneyShort(inPeriod(r.won_arr, view.period))}
              n={`${r.won_n} won${r.won_without_revenue ? `, ${r.won_without_revenue} no plan` : ''}`}
            />
          )
        },
      },
      {
        field: 'winSort',
        headerName: 'Win rate',
        width: 120 + INFO_HEADER_ALLOWANCE,
        renderHeader: headerWithTerm('Win rate', 'win-rate'),
        renderCell: ({ row }: { row: ChannelRow }) => {
          if (row.parentId && !row.socialNetwork) return ''
          const r = row.pipeline
          if (!flow.data) return flow.isLoading ? <Skeleton height={14} width="60%" /> : EMPTY_VALUE
          if (!r || r.win_rate === null) return EMPTY_VALUE
          return <Figure main={pct(r.won_n, r.closed_n)} n={`${r.won_n} of ${r.closed_n} closed`} small={isSmallSample(r.closed_n)} />
        },
      },
      {
        field: 'veraVersion',
        headerName: "Vera's version",
        type: 'string',
        flex: 1,
        // Sized so all six columns fit the 1280px desktop (§37) without a sideways scroll:
        // the Page column is the one action on the row and must not sit off-screen.
        minWidth: 232,
        sortable: false,
        renderCell: ({ row }: { row: GtmChannel }) => (
          <Typography variant="body3" color="text.secondary" noWrap title={row.veraVersion}>
            {row.veraVersion}
          </Typography>
        ),
      },
      {
        field: 'metric',
        headerName: 'Headline',
        type: 'string',
        width: 208,
        sortable: false,
        renderCell: ({ row }: { row: GtmChannel }) => {
          if (row.metric === 'articles') {
            const total = ARTICLES_MANIFEST.articles.length
            const live = ARTICLES_MANIFEST.articles.filter((a) => a.status === 'published').length
            return (
              <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
                <Typography variant="body3" noWrap>
                  {total} articles · {live} published
                </Typography>
                <Typography variant="body4" color="text.secondary" noWrap>
                  Website snapshot {formatIsoDay(ARTICLES_MANIFEST_PROVENANCE.generatedOn)}
                </Typography>
              </Stack>
            )
          }
          if (row.metric === 'trade-shows') {
            return (
              <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
                <Typography variant="body3" noWrap>
                  {TRADE_SHOWS_SUMMARY.events} events · {TRADE_SHOWS_SUMMARY.planned} attending
                </Typography>
                <Typography variant="body4" color="text.secondary" noWrap>
                  Prices checked {TRADE_SHOWS_SUMMARY.priceCheckedOn.includes(' to ') ? TRADE_SHOWS_SUMMARY.priceCheckedOn : formatIsoDay(TRADE_SHOWS_SUMMARY.priceCheckedOn)}
                </Typography>
              </Stack>
            )
          }
          if (row.metric === 'social-visits' && row.socialNetwork) {
            if (visitsUnavailable) {
              return (
                <Typography variant="body3" color="text.secondary" noWrap title={visitsUnavailable}>
                  {EMPTY_VALUE}
                </Typography>
              )
            }
            if (!visits) return overview.isLoading ? <Skeleton height={14} width="60%" /> : EMPTY_VALUE
            const n = visits[row.socialNetwork]
            return (
              <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }} data-social-visits={row.socialNetwork}>
                <Typography variant="body3" noWrap>
                  {n.toLocaleString('en-US')} website {n === 1 ? 'visit' : 'visits'}
                </Typography>
                <Typography variant="body4" color="text.secondary" noWrap>
                  From its referrers, {visitsRangeLabel}
                </Typography>
              </Stack>
            )
          }
          if (row.metric !== 'seo') return EMPTY_VALUE
          if (seoLoading) {
            return (
              <Stack justifyContent="center" height="100%" spacing={1} sx={{ width: '100%' }}>
                <Skeleton height={14} width="70%" />
                <Skeleton height={12} width="90%" />
              </Stack>
            )
          }
          if (seoError || !seoHeadline) return EMPTY_VALUE
          return (
            <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
              <Typography variant="body3" noWrap>
                {seoHeadline.primary}
              </Typography>
              <Typography variant="body4" color="text.secondary" noWrap>
                {seoHeadline.secondary}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: 'link',
        headerName: 'Page',
        type: 'string',
        width: 88,
        // VRG-696: ten columns no longer fit 1280 (§37), so the row's one action stays pinned
        // in view while the figures scroll, rather than sitting off-screen.
        pinned: 'right',
        sortable: false,
        renderCell: ({ row }: { row: GtmChannel }) =>
          row.link ? <LinkCell route={row.link} link="Open" align="left" /> : EMPTY_VALUE,
      },
    ],
    [seoLoading, seoError, seoHeadline, flow.data, flow.isLoading, view.period, P, visits, visitsUnavailable, overview.isLoading, visitsRangeLabel],
  )

  return (
    <>
      <Header
        title="Channels"
        description="Every go-to-market channel and where Vera stands on it, with the deals each one brought. Channels Vera works on get their own page in the sidebar."
      />
      <Stack spacing={6} useFlexGap>
      {/* VRG-701 — one filter bar for the page: the tiles, the chart and the table share it. */}
      <ChannelsFilterBar data={flow.data} api={masterApi} />
      <MasterPipelineCard data={flow.data} error={flow.error} isFetching={flow.isFetching} api={masterApi} />
      {unattributed.length > 0 && flow.data && (
        <Typography variant="body3" color="text.secondary">
          Not on a channel row:{' '}
          {unattributed
            .map((r) => `${MASTER_UNATTRIBUTED_LABELS[r.key as keyof typeof MASTER_UNATTRIBUTED_LABELS]} ${r.deals} (${pct(r.deals, flow.data!.master.total.n)})`)
            .join(', ')}
          .
        </Typography>
      )}
      {socialNotSplit > 0 && (
        <Typography variant="body3" color="text.secondary" data-social-not-split="true">
          Social deals with no network named (set to Social by hand, with no referrer or link to read one from): {socialNotSplit}. They count on the Social row,
          not on LinkedIn, Instagram or Other social.
        </Typography>
      )}
      {/* VRG-735 — the row `?social=` points at is marked (Social → Metrics links to it). */}
      <Box sx={{ '& .gtm-channel-focus': { bgcolor: 'action.selected' } }}>
        <Table
          // Not `fillBottom` any more (§22): the master pipeline sits above, so the table is no
          // longer the page's only content. Tall enough for all twenty rows (72px each, the
          // shared Table's row height) plus its toolbar and header, so the page scrolls, not it.
          height={TABLE_CHROME_PX + GTM_CHANNELS.length * 72}
          rows={rows}
          columns={columns}
          getRowId={(row: ChannelRow) => row.id}
          getRowClassName={({ id }: { id: string }) => (id === focusRowId ? 'gtm-channel-focus' : '')}
          exportFilename="gtm-channels"
          emptyState={
            <EmptyState
              title="No channels match"
              text="Clear the search or the status filter to see every channel."
              icon={<RadioTowerIcon strokeWidth={1} size={32} />}
            />
          }
        />
      </Box>
      </Stack>
    </>
  )
}
